import { formatCurrency, formatDate } from './formatters'
import { MOCK_JOBS, MOCK_POS } from './mockData'
import {
  ARCHIVE_OUTPUT_META,
  ARCHIVE_PROFILE_META,
  type ArchiveRequest,
  getJobArchiveStatus,
} from './mockDataArchive'
import { appendArchiveRegisterEntry } from './archiveRegistry'
import {
  MOCK_CONTRACTS,
  MOCK_EOT_CLAIMS,
  MOCK_PROGRESS_CLAIMS,
  MOCK_VARIATIONS,
} from './mockDataCommercial'
import {
  MOCK_EMPS,
  MOCK_ENV_INCIDENTS,
  MOCK_ENV_MONITORING,
  MOCK_INCIDENTS,
  MOCK_ITP,
  MOCK_NCRS,
  MOCK_SITE_DIARIES,
  MOCK_SWMS,
  MOCK_TOOLBOX_TALKS,
} from './mockDataTier3'
import {
  MOCK_PERMITS,
  MOCK_WHS_INSPECTIONS,
  MOCK_WHS_NOTIFICATIONS,
  MOCK_WORKER_INDUCTIONS,
} from './mockDataWHS'

type ArchiveBundleResult = {
  filename: string
  blob: Blob
}

type BundleEntry = {
  path: string
  content: string
}

const encoder = new TextEncoder()
const crcTable = buildCrc32Table()

function buildCrc32Table() {
  const table = new Uint32Array(256)
  for (let i = 0; i < 256; i += 1) {
    let c = i
    for (let j = 0; j < 8; j += 1) {
      c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1)
    }
    table[i] = c >>> 0
  }
  return table
}

function crc32(data: Uint8Array) {
  let crc = 0xffffffff
  for (const byte of data) {
    crc = crcTable[(crc ^ byte) & 0xff] ^ (crc >>> 8)
  }
  return (crc ^ 0xffffffff) >>> 0
}

function writeU16(view: DataView, offset: number, value: number) {
  view.setUint16(offset, value, true)
}

function writeU32(view: DataView, offset: number, value: number) {
  view.setUint32(offset, value >>> 0, true)
}

function toDosDate(date: Date) {
  const year = Math.max(date.getFullYear(), 1980)
  return ((year - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate()
}

function toDosTime(date: Date) {
  return (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2)
}

function buildZip(entries: BundleEntry[]) {
  const localParts: Uint8Array[] = []
  const centralParts: Uint8Array[] = []
  let localOffset = 0

  for (const entry of entries) {
    const data = encoder.encode(entry.content)
    const nameBytes = encoder.encode(entry.path)
    const fileDate = new Date()
    const dosDate = toDosDate(fileDate)
    const dosTime = toDosTime(fileDate)
    const checksum = crc32(data)

    const localHeader = new Uint8Array(30 + nameBytes.length + data.length)
    const localView = new DataView(localHeader.buffer)
    writeU32(localView, 0, 0x04034b50)
    writeU16(localView, 4, 20)
    writeU16(localView, 6, 0)
    writeU16(localView, 8, 0)
    writeU16(localView, 10, dosTime)
    writeU16(localView, 12, dosDate)
    writeU32(localView, 14, checksum)
    writeU32(localView, 18, data.length)
    writeU32(localView, 22, data.length)
    writeU16(localView, 26, nameBytes.length)
    writeU16(localView, 28, 0)
    localHeader.set(nameBytes, 30)
    localHeader.set(data, 30 + nameBytes.length)
    localParts.push(localHeader)

    const centralHeader = new Uint8Array(46 + nameBytes.length)
    const centralView = new DataView(centralHeader.buffer)
    writeU32(centralView, 0, 0x02014b50)
    writeU16(centralView, 4, 20)
    writeU16(centralView, 6, 20)
    writeU16(centralView, 8, 0)
    writeU16(centralView, 10, 0)
    writeU16(centralView, 12, dosTime)
    writeU16(centralView, 14, dosDate)
    writeU32(centralView, 16, checksum)
    writeU32(centralView, 20, data.length)
    writeU32(centralView, 24, data.length)
    writeU16(centralView, 28, nameBytes.length)
    writeU16(centralView, 30, 0)
    writeU16(centralView, 32, 0)
    writeU16(centralView, 34, 0)
    writeU16(centralView, 36, 0)
    writeU32(centralView, 38, 0)
    writeU32(centralView, 42, localOffset)
    centralHeader.set(nameBytes, 46)
    centralParts.push(centralHeader)

    localOffset += localHeader.length
  }

  const centralOffset = localOffset
  const centralSize = centralParts.reduce((sum, part) => sum + part.length, 0)
  const endRecord = new Uint8Array(22)
  const endView = new DataView(endRecord.buffer)
  writeU32(endView, 0, 0x06054b50)
  writeU16(endView, 4, 0)
  writeU16(endView, 6, 0)
  writeU16(endView, 8, entries.length)
  writeU16(endView, 10, entries.length)
  writeU32(endView, 12, centralSize)
  writeU32(endView, 16, centralOffset)
  writeU16(endView, 20, 0)

  const blobParts: BlobPart[] = [
    ...localParts.map((part) => part.buffer.slice(part.byteOffset, part.byteOffset + part.byteLength) as ArrayBuffer),
    ...centralParts.map((part) => part.buffer.slice(part.byteOffset, part.byteOffset + part.byteLength) as ArrayBuffer),
    endRecord.buffer.slice(endRecord.byteOffset, endRecord.byteOffset + endRecord.byteLength) as ArrayBuffer,
  ]
  return new Blob(blobParts, { type: 'application/zip' })
}

function downloadBlob(blob: Blob, filename: string) {
  const href = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = href
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(href), 1000)
}

function slugify(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

function escapeHtml(value: string) {
  return value
    .split('&').join('&amp;')
    .split('<').join('&lt;')
    .split('>').join('&gt;')
    .split('"').join('&quot;')
    .split("'").join('&#39;')
}

function buildRows(rows: string[][]) {
  if (rows.length === 0) {
    return '<tr><td colspan="6">No records in this archive slice.</td></tr>'
  }
  return rows.map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`).join('')
}

export function buildAndDownloadArchivePackage(jobId: string, archiveRequest?: ArchiveRequest): ArchiveBundleResult {
  const job = MOCK_JOBS.find((item) => item.id === jobId)
  if (!job) {
    throw new Error(`Job not found for archive package: ${jobId}`)
  }

  const archiveStatus = getJobArchiveStatus(jobId)
  const contract = MOCK_CONTRACTS.find((item) => item.jobId === jobId) ?? null
  const purchaseOrders = MOCK_POS.filter((item) => item.jobId === jobId)
  const variations = MOCK_VARIATIONS.filter((item) => item.jobId === jobId)
  const progressClaims = MOCK_PROGRESS_CLAIMS.filter((item) => item.jobId === jobId)
  const eotClaims = MOCK_EOT_CLAIMS.filter((item) => item.jobId === jobId)
  const permits = MOCK_PERMITS.filter((item) => item.jobId === jobId)
  const inductions = MOCK_WORKER_INDUCTIONS.filter((item) => item.jobId === jobId)
  const inspections = MOCK_WHS_INSPECTIONS.filter((item) => item.jobId === jobId)
  const whsNotifications = MOCK_WHS_NOTIFICATIONS.filter((item) => item.jobId === jobId)
  const swms = MOCK_SWMS.filter((item) => item.jobId === jobId)
  const toolboxTalks = MOCK_TOOLBOX_TALKS.filter((item) => item.jobId === jobId)
  const siteDiaries = MOCK_SITE_DIARIES.filter((item) => item.jobId === jobId)
  const incidents = MOCK_INCIDENTS.filter((item) => item.jobId === jobId)
  const ncrs = MOCK_NCRS.filter((item) => item.jobId === jobId)
  const environmentalPlan = MOCK_EMPS.find((item) => item.jobId === jobId) ?? null
  const environmentalMonitoring = MOCK_ENV_MONITORING.filter((item) => item.jobId === jobId)
  const environmentalIncidents = MOCK_ENV_INCIDENTS.filter((item) => item.jobId === jobId)
  const itp = MOCK_ITP.jobId === jobId ? MOCK_ITP : null

  const requestLabel = archiveRequest?.targetPeriodLabel ?? 'Current package'
  const packageKind = archiveRequest?.requestType ?? 'PROJECT_ARCHIVE'
  const profile = archiveRequest ? ARCHIVE_PROFILE_META[archiveRequest.profileId] : null
  const requestedOutputs = archiveRequest?.outputs ?? ['REPORT_PACK', 'ARCHIVE_ZIP', 'QAQC_ZIP', 'READ_ONLY_DASHBOARD']
  const generatedAt = new Date().toISOString()
  const generatedDate = formatDate(generatedAt)

  const packageSummary = {
    job,
    archiveStatus,
    archiveRequest: archiveRequest ?? null,
    generatedAt,
    requestedOutputs,
    counts: {
      purchaseOrders: purchaseOrders.length,
      variations: variations.length,
      progressClaims: progressClaims.length,
      permits: permits.length,
      inspections: inspections.length,
      swms: swms.length,
      siteDiaries: siteDiaries.length,
      incidents: incidents.length,
      ncrs: ncrs.length,
    },
  }

  const commercialRows = [
    ...purchaseOrders.map((po) => [po.poNumber, 'Purchase order', po.status, formatCurrency(po.totalExGST), po.createdAt, po.supplierName]),
    ...variations.map((variation) => [variation.variationNumber, 'Variation', variation.status, formatCurrency(variation.totalExGST), variation.noticeDueDate ?? '-', variation.description]),
    ...progressClaims.map((claim) => [`Claim ${claim.claimNumber}`, 'Progress claim', claim.status, formatCurrency(claim.totalIncGST), claim.claimDate, `${claim.percentComplete}% complete`]),
    ...eotClaims.map((claim) => [claim.claimNumber, 'EOT claim', claim.status, `${claim.daysClaimedExtension} day(s)`, claim.delayEventDate, claim.delayEvent]),
  ]

  const qaqcRows = [
    ...permits.map((permit) => [permit.permitNumber, 'Permit', permit.status, permit.type, permit.plannedStart, permit.description]),
    ...inspections.map((inspection) => [
      inspection.inspectionNumber,
      'Inspection',
      inspection.result ?? inspection.status,
      inspection.type,
      inspection.conductedAt,
      inspection.issues[0]?.location ?? inspection.notes ?? 'Inspection record',
    ]),
    ...swms.map((item) => [item.swmsNumber, 'SWMS', item.status, `Rev ${item.revision}`, item.approvedAt ?? '-', item.title]),
    ...siteDiaries.map((diary) => [diary.date, 'Site diary', diary.status, `${diary.personnel.length} personnel`, diary.date, diary.progressNotes]),
    ...ncrs.map((ncr) => [ncr.ncrNumber, 'NCR', ncr.status, ncr.source, ncr.date, ncr.description]),
  ]

  const readme = [
    `MCBCE archive bundle`,
    ``,
    `Job: ${job.jobNumber} - ${job.description}`,
    `Generated: ${generatedDate}`,
    `Request label: ${requestLabel}`,
    `Package type: ${packageKind}`,
    profile ? `Profile: ${profile.label}` : `Profile: Ad hoc local export`,
    ``,
    `This zip is designed to unzip and open on Windows, macOS, or Linux.`,
    `Start with: dashboard/index.html`,
    ``,
    `Included outputs:`,
    ...requestedOutputs.map((output) => `- ${ARCHIVE_OUTPUT_META[output].label}`),
    ``,
    `Data folders:`,
    `- data/project.json`,
    `- data/commercial.json`,
    `- data/qaqc.json`,
    `- data/archive-manifest.json`,
    ``,
    `Report folders:`,
    `- reports/project-summary.html`,
    `- reports/registers/commercial-register.csv`,
    `- reports/registers/qaqc-register.csv`,
  ].join('\n')

  const dashboardHtml = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(job.jobNumber)} archive dashboard</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 0; background: #f3f5f7; color: #17212b; }
    .wrap { max-width: 1180px; margin: 0 auto; padding: 32px 24px 48px; }
    .hero { background: #0f172a; color: #fff; border-radius: 12px; padding: 24px; margin-bottom: 24px; }
    .hero p { margin: 6px 0 0; color: #cbd5e1; }
    .grid { display: grid; gap: 16px; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); margin-bottom: 24px; }
    .card { background: #fff; border: 1px solid #d9e1e8; border-radius: 12px; padding: 16px; }
    .label { font-size: 12px; color: #64748b; text-transform: uppercase; }
    .value { font-size: 28px; font-weight: 700; margin-top: 6px; }
    .section { background: #fff; border: 1px solid #d9e1e8; border-radius: 12px; padding: 18px; margin-bottom: 18px; }
    h1, h2, h3 { margin: 0; }
    h2 { font-size: 18px; margin-bottom: 14px; }
    ul { margin: 0; padding-left: 18px; }
    li { margin: 6px 0; }
    table { width: 100%; border-collapse: collapse; font-size: 14px; }
    th, td { border-bottom: 1px solid #e5e7eb; text-align: left; padding: 10px 8px; vertical-align: top; }
    th { color: #64748b; font-size: 12px; text-transform: uppercase; }
    .meta { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
  </style>
</head>
<body>
  <div class="wrap">
    <div class="hero">
      <h1>${escapeHtml(job.jobNumber)} archive dashboard</h1>
      <p>${escapeHtml(job.description)}</p>
      <p>${escapeHtml(requestLabel)} · Generated ${escapeHtml(generatedDate)}</p>
    </div>

    <div class="grid">
      <div class="card"><div class="label">Archive readiness</div><div class="value">${archiveStatus?.archiveReadyPct ?? 0}%</div></div>
      <div class="card"><div class="label">Records coverage</div><div class="value">${archiveStatus?.recordsCoveragePct ?? 0}%</div></div>
      <div class="card"><div class="label">QAQC coverage</div><div class="value">${archiveStatus?.qaqcCoveragePct ?? 0}%</div></div>
      <div class="card"><div class="label">Commercial close</div><div class="value">${archiveStatus?.commercialClosePct ?? 0}%</div></div>
      <div class="card"><div class="label">POs</div><div class="value">${purchaseOrders.length}</div></div>
      <div class="card"><div class="label">Site diaries</div><div class="value">${siteDiaries.length}</div></div>
    </div>

    <div class="section">
      <h2>Project metadata</h2>
      <div class="meta">
        <div><strong>Client</strong><br/>${escapeHtml(job.clientName)}</div>
        <div><strong>Contract</strong><br/>${escapeHtml(job.contractNumber || '-')}</div>
        <div><strong>Target end</strong><br/>${escapeHtml(job.targetEnd ? formatDate(job.targetEnd) : 'TBC')}</div>
        <div><strong>Generated output</strong><br/>${requestedOutputs.map((output) => escapeHtml(ARCHIVE_OUTPUT_META[output].label)).join(', ')}</div>
      </div>
    </div>

    <div class="section">
      <h2>Blocking items</h2>
      <ul>${(archiveStatus?.blockingItems ?? ['No blockers captured']).map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>
    </div>

    <div class="section">
      <h2>Commercial register</h2>
      <table>
        <thead><tr><th>Ref</th><th>Type</th><th>Status</th><th>Value</th><th>Date</th><th>Notes</th></tr></thead>
        <tbody>${buildRows(commercialRows)}</tbody>
      </table>
    </div>

    <div class="section">
      <h2>QAQC register</h2>
      <table>
        <thead><tr><th>Ref</th><th>Type</th><th>Status</th><th>Detail</th><th>Date</th><th>Notes</th></tr></thead>
        <tbody>${buildRows(qaqcRows)}</tbody>
      </table>
    </div>
  </div>
</body>
</html>`

  const projectSummaryHtml = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(job.jobNumber)} report pack</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 32px; color: #17212b; }
    h1, h2 { margin-bottom: 10px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
    th, td { border: 1px solid #d9e1e8; padding: 8px; text-align: left; vertical-align: top; }
    th { background: #f8fafc; }
    .summary { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; margin-bottom: 24px; }
    .box { border: 1px solid #d9e1e8; border-radius: 10px; padding: 16px; }
    ul { margin-top: 8px; }
  </style>
</head>
<body>
  <h1>${escapeHtml(job.jobNumber)} archive report pack</h1>
  <p>${escapeHtml(job.description)}</p>
  <p>Generated ${escapeHtml(generatedDate)} for ${escapeHtml(requestLabel)}.</p>

  <div class="summary">
    <div class="box">
      <h2>Project</h2>
      <p><strong>Client:</strong> ${escapeHtml(job.clientName)}</p>
      <p><strong>Project manager:</strong> ${escapeHtml(job.pmName)}</p>
      <p><strong>Contract value:</strong> ${escapeHtml(formatCurrency(job.contractValue))}</p>
      <p><strong>Progress:</strong> ${job.progressPercent}%</p>
    </div>
    <div class="box">
      <h2>Archive readiness</h2>
      <p><strong>Overall:</strong> ${archiveStatus?.archiveReadyPct ?? 0}%</p>
      <p><strong>Records:</strong> ${archiveStatus?.recordsCoveragePct ?? 0}%</p>
      <p><strong>QAQC:</strong> ${archiveStatus?.qaqcCoveragePct ?? 0}%</p>
      <p><strong>Recommended action:</strong> ${escapeHtml(archiveStatus?.recommendedAction ?? 'No recommendation')}</p>
    </div>
  </div>

  <h2>Commercial status</h2>
  <table>
    <thead><tr><th>Contract</th><th>Purchase orders</th><th>Variations</th><th>Claims</th></tr></thead>
    <tbody>
      <tr>
        <td>${escapeHtml(contract ? `${contract.contractNumber} (${formatCurrency(contract.originalContractValue + contract.approvedVariationsValue)})` : 'No contract record')}</td>
        <td>${purchaseOrders.length} record(s)</td>
        <td>${variations.length} record(s)</td>
        <td>${progressClaims.length} record(s)</td>
      </tr>
    </tbody>
  </table>

  <h2>QAQC status</h2>
  <table>
    <thead><tr><th>SWMS</th><th>Permits</th><th>Inspections</th><th>ITP</th><th>NCRs</th></tr></thead>
    <tbody>
      <tr>
        <td>${swms.length}</td>
        <td>${permits.length}</td>
        <td>${inspections.length}</td>
        <td>${itp ? itp.items.length : 0}</td>
        <td>${ncrs.length}</td>
      </tr>
    </tbody>
  </table>

  <h2>Close-out blockers</h2>
  <ul>${(archiveStatus?.blockingItems ?? ['No blockers captured']).map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>
</body>
</html>`

  const commercialCsv = [
    'Ref,Type,Status,Value,Date,Notes',
    ...commercialRows.map((row) => row.map((cell) => `"${String(cell).split('"').join('""')}"`).join(',')),
  ].join('\n')

  const qaqcCsv = [
    'Ref,Type,Status,Detail,Date,Notes',
    ...qaqcRows.map((row) => row.map((cell) => `"${String(cell).split('"').join('""')}"`).join(',')),
  ].join('\n')

  const packageSlug = slugify(`${job.jobNumber}-${archiveRequest?.targetPeriodLabel ?? packageKind}`)
  const filename = `${packageSlug || slugify(job.jobNumber)}.zip`

  const entries: BundleEntry[] = [
    { path: 'README.txt', content: readme },
    { path: 'dashboard/index.html', content: dashboardHtml },
    { path: 'reports/project-summary.html', content: projectSummaryHtml },
    { path: 'reports/registers/commercial-register.csv', content: commercialCsv },
    { path: 'reports/registers/qaqc-register.csv', content: qaqcCsv },
    { path: 'data/project.json', content: JSON.stringify({ job, contract, archiveStatus }, null, 2) },
    { path: 'data/commercial.json', content: JSON.stringify({ purchaseOrders, variations, progressClaims, eotClaims }, null, 2) },
    { path: 'data/qaqc.json', content: JSON.stringify({
      permits,
      inductions,
      inspections,
      whsNotifications,
      swms,
      toolboxTalks,
      siteDiaries,
      incidents,
      itp,
      ncrs,
      environmentalPlan,
      environmentalMonitoring,
      environmentalIncidents,
    }, null, 2) },
    { path: 'data/archive-manifest.json', content: JSON.stringify(packageSummary, null, 2) },
  ]

  const blob = buildZip(entries)
  downloadBlob(blob, filename)
  appendArchiveRegisterEntry({
    id: `arc_${Date.now()}`,
    jobId: job.id,
    jobNumber: job.jobNumber,
    jobDescription: job.description,
    filename,
    generatedAt,
    requestType: packageKind,
    requestLabel,
    profileLabel: profile?.label ?? null,
    outputs: requestedOutputs.map((output) => ARCHIVE_OUTPUT_META[output].label),
    archiveReadyPct: archiveStatus?.archiveReadyPct ?? null,
    sourceRequestId: archiveRequest?.id ?? null,
    archiveRequestSnapshot: archiveRequest ?? null,
    counts: packageSummary.counts,
  })
  return { filename, blob }
}
